import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { TenantModule } from '@models';
import { TenantFeaturesService } from '@services';
import { Observable, filter, first, map } from 'rxjs';

/**
 * Route guard that blocks access when none of the required modules are active.
 * Pass a single module or an array for OR semantics (any one is enough).
 * Redirects to /dashboard/home when access is denied.
 *
 * @example — single module
 *   canActivate: [moduleGuard('transfers')]
 *
 * @example — OR: either module unlocks the route (e.g. route shared across tenant types)
 *   canActivate: [moduleGuard(['advanced_reports', 'reports'])]
 *
 * ── Convention ────────────────────────────────────────────────────────────────
 * Every route section that corresponds to a module MUST be gated with this guard.
 * When adding a new menu option that maps to a module:
 *   1. Add the module key to FEDERATION_MODULES or CLUB_MODULES in libs/models/src/lib/tenant.ts
 *   2. Add a label to FEDERATION_MODULE_LABELS or CLUB_MODULE_LABELS
 *   3. Gate the route with canActivate: [moduleGuard('<new_module>')]
 *   4. Gate the menu item with `visible: ... && has('<new_module>')` in menu.component.ts
 */
export function moduleGuard(
  module: TenantModule | TenantModule[],
): CanActivateFn {
  const modules = Array.isArray(module) ? module : [module];
  return (): Observable<boolean | UrlTree> => {
    const features = inject(TenantFeaturesService);
    const router = inject(Router);

    // Wait for tenantConfig$ to resolve (non-null) before deciding.
    // hasModule$() emits false while config loads — we skip that initial emission
    // by filtering on the config directly.
    return features.tenantConfig$.pipe(
      filter((cfg): cfg is NonNullable<typeof cfg> => cfg !== null),
      first(),
      map((cfg) => {
        const hasAny = modules.some((m) => features.hasModule(m, cfg));
        if (hasAny) return true;
        return router.createUrlTree(['/dashboard/home']);
      }),
    );
  };
}
